import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios';

import { getApiBaseUrl, getApiClient } from '@treneo/infrastructure/api';
import { logger } from '@treneo/shared/logger';

import {
  ALLOWED_WHEN_LOGOUT_PATHS,
  ALLOWED_WHEN_REFRESH_FAILED_PATHS,
  AUTH_REFRESH_PATH,
  CURRENT_USER_PATH,
  isCurrentUserEndpoint,
  isLoginEndpoint,
  isLogoutEndpoint,
  isRefreshEndpoint,
  isRegistrationEndpoint,
  isUrlAllowed
} from '../api-paths.constants';
import type { LogoutHandler } from '../data/types';

import { TokenManager } from './token-manager';

export type GetIsLoggedIn = () => boolean;

interface QueuedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}

const POST_LOGIN_GRACE_MS = 5000;
const QUEUED_REQUEST_TIMEOUT_MS = 25000;
const REFRESH_401_RETRY_DELAY_MS = 2000;

const generateTraceId = (): string => {
  const uuid =
    globalThis.crypto?.randomUUID?.() ?? `${Math.random().toString(16)}${Date.now().toString(16)}`;
  return uuid.replace(/-/g, '').padEnd(32, '0').slice(0, 32);
};

const generateSpanId = (): string => {
  const random = Math.random().toString(16);
  return random.replace(/0x/i, '').padEnd(16, '0').slice(0, 16);
};

export class AxiosConfigurator {
  private readonly tokenManager = new TokenManager();
  private readonly logoutHandler: LogoutHandler;
  private readonly getIsLoggedIn: GetIsLoggedIn | undefined;
  private axiosInterceptor: number | null = null;
  private apiClientSetup = false;
  private interceptorsInitialized = false;
  private recent401Errors: number[] = [];
  private readonly MAX_401_COUNT = 10;
  private readonly MAX_401_WINDOW_MS = 10000;
  private refreshPromise: Promise<void> | null = null;
  private queuedRequests: QueuedRequest[] = [];
  private isLoggingOut = false;
  private refreshFailed = false;
  private lastSuccessfulLoginOrRegisterTime = 0;
  private refresh401Retried = false;

  constructor(logoutHandler: LogoutHandler, getIsLoggedIn?: GetIsLoggedIn) {
    this.logoutHandler = logoutHandler;
    this.getIsLoggedIn = getIsLoggedIn;
  }

  public getTokenManager(): TokenManager {
    return this.tokenManager;
  }

  public async initializeInterceptors(): Promise<void> {
    if (this.interceptorsInitialized) {
      return;
    }

    this.setupInterceptors();
    await this.setupApiClientInterceptors();
    this.interceptorsInitialized = true;
  }

  public setupInstanceInterceptors(axiosInstance: AxiosInstance): AxiosInstance {
    axiosInstance.interceptors.request.use(
      this.onRequestWithAuth.bind(this),
      this.onRequestError.bind(this)
    );

    axiosInstance.interceptors.response.use(
      this.onResponse.bind(this),
      this.onResponseError.bind(this)
    );

    return axiosInstance;
  }

  public clearTokenCache(): void {
    this.tokenManager.clearCache();
  }

  public clearRefreshState(): void {
    this.refreshPromise = null;
    this.queuedRequests = [];
    if (!this.isLoggingOut) {
      this.recent401Errors = [];
      this.refreshFailed = false;
      this.refresh401Retried = false;
    }
  }

  public resetLogoutState(): void {
    if (this.isLoggingOut) {
      logger.warn('[Logout State] Resetting logout state, allowing new login attempts');
    }
    this.isLoggingOut = false;
    this.refreshFailed = false;
    this.refresh401Retried = false;
  }

  private async setupApiClientInterceptors(): Promise<void> {
    if (this.apiClientSetup) {
      return;
    }

    const apiClient = getApiClient();
    try {
      await apiClient.setupStoreInterceptors(this.logoutHandler, async (instance: AxiosInstance) =>
        this.setupInstanceInterceptors(instance)
      );
      this.apiClientSetup = true;
    } catch (error) {
      logger.warn('Failed to setup API client store interceptors:', error);
    }
  }

  private setupInterceptors(): void {
    if (this.axiosInterceptor !== null) {
      axios.interceptors.response.eject(this.axiosInterceptor);
    }

    this.axiosInterceptor = axios.interceptors.response.use(
      this.onResponse.bind(this),
      this.onResponseError.bind(this)
    );
  }

  private async onRequestWithAuth(
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    const url = config.url ?? '';

    if (isLoginEndpoint(url) || isRegistrationEndpoint(url)) {
      if (this.isLoggingOut) {
        logger.warn(
          '[Request] Login/Registration endpoint detected while logging out, resetting logout state to allow login'
        );
        this.resetLogoutState();
      }
    }

    if (this.isLoggingOut) {
      const isAllowed =
        isUrlAllowed(url, ALLOWED_WHEN_LOGOUT_PATHS) ||
        isLoginEndpoint(url) ||
        isRegistrationEndpoint(url);
      if (!isAllowed) {
        const err = new Error('auth.requestBlockedLogoutInProgress') as Error & {
          config: InternalAxiosRequestConfig;
          isAxiosError: boolean;
          response: { status: number; statusText: string; data: { detail: string } };
        };
        err.config = config;
        err.isAxiosError = true;
        err.response = {
          status: 401,
          statusText: 'Unauthorized',
          data: { detail: 'auth.requestBlockedLogoutInProgress' }
        };
        throw err;
      }
    }

    if (this.refreshFailed) {
      const urlForRefresh = config.url ?? '';
      if (!isUrlAllowed(urlForRefresh, ALLOWED_WHEN_REFRESH_FAILED_PATHS)) {
        const err = new Error('Request blocked: refresh failed, logout required') as Error & {
          config: InternalAxiosRequestConfig;
          isAxiosError: boolean;
          response: { status: number; statusText: string };
        };
        err.config = config;
        err.isAxiosError = true;
        err.response = { status: 401, statusText: 'Unauthorized' };
        throw err;
      }
    }

    if (!config.headers) {
      config.headers = {} as InternalAxiosRequestConfig['headers'];
    }

    const method = config.method?.toUpperCase();
    if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
      try {
        const csrfToken = await this.tokenManager.getToken();
        if (csrfToken) {
          config.headers['X-CSRF-TOKEN'] = csrfToken;
        }
      } catch (error) {
        logger.warn('Failed to add CSRF token:', error);
      }
    }

    const headersRecord = config.headers as Record<string, unknown>;
    if (!('traceparent' in headersRecord)) {
      const traceId = generateTraceId();
      const spanId = generateSpanId();
      const traceFlags = '01';
      const version = '00';

      headersRecord.traceparent = `${version}-${traceId}-${spanId}-${traceFlags}`;
    }

    return config;
  }

  private onRequestError(error: AxiosError): Promise<AxiosError> {
    if (
      error.message?.includes('Request blocked') ||
      error.message?.includes('auth.requestBlockedLogoutInProgress')
    ) {
      const translationKey = error.message.startsWith('auth.')
        ? error.message
        : 'auth.requestBlockedLogoutInProgress';
      const blockedError = Object.assign(error, {
        response: {
          status: 401,
          statusText: 'Unauthorized',
          data: { detail: translationKey }
        },
        isAxiosError: true
      });
      return Promise.reject(blockedError);
    }
    throw error;
  }

  private onResponse(response: AxiosResponse): AxiosResponse {
    const url = response.config?.url ?? '';
    if (
      (isLoginEndpoint(url) || isRegistrationEndpoint(url)) &&
      response.status >= 200 &&
      response.status < 300
    ) {
      this.lastSuccessfulLoginOrRegisterTime = Date.now();
      if (this.isLoggingOut || this.refreshFailed) {
        logger.warn('[Response] Successful login/registration detected, resetting logout state');
        this.resetLogoutState();
      }
    }

    return response;
  }

  private async onResponseError(error: AxiosError): Promise<AxiosError | AxiosResponse<unknown>> {
    if (error.message?.includes('Request blocked')) {
      const err = error as AxiosError & { response?: AxiosResponse };
      err.response ??= {
        status: 401,
        statusText: 'Unauthorized',
        data: { message: error.message }
      } as AxiosResponse;
      return this.handleUnauthorizedError(err);
    }

    const response = (error.response as AxiosResponse) ?? error;

    if (response?.status === 401) {
      return this.handleUnauthorizedError(error);
    }

    if (response?.status === 403) {
      return this.handleForbiddenError(error);
    }

    if (response?.status === 502 || response?.status === 503) {
      return this.handle502Or503Error(error);
    }

    throw error;
  }

  private async handle502Or503Error(
    error: AxiosError
  ): Promise<AxiosError | AxiosResponse<unknown>> {
    const config = error.config as InternalAxiosRequestConfig & { _retried502_503?: boolean };
    if (config?._retried502_503) {
      logger.warn('[502/503] Already retried once, propagating error');
      throw error;
    }

    if (!config || !config.url) {
      throw error;
    }

    if (this.isLoggingOut || this.refreshFailed) {
      throw error;
    }

    try {
      logger.warn('[502/503] One attempt: refresh then retry request', config.url);
      await this.performRefresh();
      if (this.isLoggingOut || this.refreshFailed) {
        throw error;
      }

      const retryConfig = { ...config, _retried502_503: true as const };
      retryConfig.withCredentials = true;
      retryConfig.baseURL ??= getApiBaseUrl();

      const response = await getApiClient().getInstance().request(retryConfig);
      return response as AxiosResponse;
    } catch (retryError: unknown) {
      if (this.isLoggingOut || this.refreshFailed) {
        throw retryError instanceof Error ? retryError : error;
      }
      const isAxiosErr =
        retryError != null &&
        typeof retryError === 'object' &&
        'isAxiosError' in retryError &&
        (retryError as { isAxiosError?: boolean }).isAxiosError === true;
      throw isAxiosErr ? (retryError as AxiosError) : error;
    }
  }

  private async handleUnauthorizedError(
    error: AxiosError
  ): Promise<AxiosError | AxiosResponse<unknown>> {
    if (this.isLoggingOut) {
      logger.warn('[401 Handler] Already logging out, rejecting request:', error.config?.url);
      throw error;
    }

    if (!error.config) {
      logger.warn('[401 Handler] No config, checking logout threshold');
      this.checkAndLogoutIfNeeded();
      throw error;
    }

    const url = error.config.url ?? '';
    const isRefresh = isRefreshEndpoint(url);
    const isLogout = isLogoutEndpoint(url);
    const isLogin = isLoginEndpoint(url);

    logger.warn(
      `[401 Handler] 401 error for: ${url}, refreshFailed: ${this.refreshFailed}, recent401Errors: ${this.recent401Errors.length}`
    );

    if (!isRefresh && !isLogout && !isLogin) {
      if (this.getIsLoggedIn && !this.getIsLoggedIn()) {
        logger.warn(
          '[401 Handler] Not logged in, skipping refresh and logout (e.g. bootstrap/getCurrentUser 401)'
        );
        throw error;
      }
      if (this.checkAndLogoutIfNeeded()) {
        throw error;
      }
    }

    if (isRefresh) {
      const retryAfter = error?.response?.headers?.['retry-after'];
      if (retryAfter && Number.parseInt(retryAfter) > 0) {
        logger.warn(
          `Refresh endpoint returned 401 with retry-after header, will retry after ${retryAfter} seconds`
        );
        throw error;
      }

      if (!this.refresh401Retried) {
        this.refresh401Retried = true;
        logger.warn(
          `[401 Handler] Refresh returned 401, retrying once after ${REFRESH_401_RETRY_DELAY_MS} ms (concurrent request may have updated cookies)`
        );
        await new Promise<void>(resolve => {
          globalThis.setTimeout(resolve, REFRESH_401_RETRY_DELAY_MS);
        });
        try {
          await this.performRefresh();
          this.refresh401Retried = false;
          throw error;
        } catch (retryError: unknown) {
          const status = (retryError as { response?: { status?: number } })?.response?.status;
          if (status === 401) {
            logger.warn('[401 Handler] Refresh retry still returned 401, logging out');
            this.refreshFailed = true;
            this.logoutImmediately();
          }
          throw retryError;
        }
      }

      logger.warn('Refresh endpoint returned 401, logging out');
      this.refreshFailed = true;
      this.logoutImmediately();
      throw error;
    }

    if (isLogout) {
      logger.warn('Logout endpoint returned 401, ignoring and proceeding with logout');
      throw error;
    }

    if (isLogin) {
      logger.warn(
        'Login endpoint returned 401, allowing error to propagate (user not authenticated yet)'
      );
      this.resetLogoutState();
      throw error;
    }

    const isCurrentUser = isCurrentUserEndpoint(url);
    const withinPostLoginGrace =
      this.lastSuccessfulLoginOrRegisterTime > 0 &&
      Date.now() - this.lastSuccessfulLoginOrRegisterTime < POST_LOGIN_GRACE_MS;
    if (isCurrentUser && withinPostLoginGrace) {
      logger.warn(
        `[401 Handler] 401 on ${CURRENT_USER_PATH} shortly after login, propagating without logout (caller may retry)`
      );
      throw error;
    }

    if (this.refreshFailed) {
      logger.warn('Refresh already failed, logging out immediately');
      this.logoutImmediately();
      throw error;
    }

    if (this.refreshPromise) {
      if (this.refreshFailed || this.isLoggingOut) {
        throw error;
      }

      const requestConfig = error.config;
      if (!requestConfig) {
        throw error;
      }

      return new Promise<AxiosResponse>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          const idx = this.queuedRequests.findIndex(r => r.config === requestConfig);
          if (idx >= 0) {
            this.queuedRequests.splice(idx, 1);
            reject(new Error('Request cancelled: queue timeout waiting for token refresh'));
          }
        }, QUEUED_REQUEST_TIMEOUT_MS);
        this.queuedRequests.push({
          resolve: (value: unknown) => {
            clearTimeout(timeoutId);
            resolve(value as AxiosResponse);
          },
          reject: (reason?: unknown) => {
            clearTimeout(timeoutId);
            reject(reason);
          },
          config: requestConfig
        });
      })
        .then(response => response as AxiosResponse)
        .catch(err => {
          throw err;
        });
    }

    try {
      this.refreshPromise = this.performRefresh();

      await this.refreshPromise;

      if (this.isLoggingOut || this.refreshFailed) {
        throw new Error('Logout in progress after refresh');
      }

      await new Promise<void>(resolve => {
        globalThis.setTimeout(resolve, 0);
      });

      const retryConfig = { ...error.config };
      retryConfig.withCredentials = true;
      retryConfig.baseURL ??= getApiBaseUrl();

      const response = await getApiClient().getInstance().request(retryConfig);

      this.recent401Errors = [];
      return response as AxiosResponse;
    } catch (refreshError: unknown) {
      if (this.isLoggingOut) {
        throw refreshError;
      }

      const status = (refreshError as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        const refreshErr = refreshError as { response?: { headers?: Record<string, string> } };
        const retryAfter = refreshErr?.response?.headers?.['retry-after'];
        if (retryAfter && Number.parseInt(retryAfter) > 0) {
          logger.warn(
            `Token refresh failed with 401, retrying after ${retryAfter} seconds instead of logging out`
          );
          throw refreshError;
        }

        logger.warn('Token refresh failed with 401, logging out');
        this.refreshFailed = true;
        this.logoutImmediately();
      } else if (status && status >= 500) {
        logger.warn('Server error during token refresh, will retry:', refreshError);
        throw refreshError;
      } else if ((refreshError as { response?: unknown })?.response) {
        if (this.checkAndLogoutIfNeeded()) {
          logger.warn('[401 Handler] Too many 401 errors after refresh failure, logout triggered');
        }
      } else {
        const message =
          (refreshError instanceof Error ? refreshError.message : String(refreshError)) ?? '';
        const isQueueTimeout =
          typeof message === 'string' &&
          message.includes('queue timeout') &&
          message.includes('token refresh');
        if (isQueueTimeout) {
          logger.warn(
            '[401 Handler] Queue timeout waiting for token refresh, treating as refresh failure and logging out'
          );
          this.refreshFailed = true;
          this.logoutImmediately();
        } else {
          logger.warn('Network error during token refresh, will retry:', refreshError);
        }
        throw refreshError;
      }

      throw refreshError;
    } finally {
      this.refreshPromise = null;
      this.processQueue();
    }
  }

  private async performRefresh(): Promise<void> {
    try {
      await getApiClient().post(
        AUTH_REFRESH_PATH,
        {},
        {
          timeout: 10000,
          withCredentials: true
        }
      );
      this.recent401Errors = [];
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        this.refreshFailed = true;
      }
      throw error;
    }
  }

  private processQueue(): void {
    if (this.isLoggingOut || this.refreshFailed) {
      const requests = [...this.queuedRequests];
      this.queuedRequests = [];
      requests.forEach(request => {
        try {
          request.reject(new Error('Request cancelled: logout in progress'));
        } catch (error) {
          logger.warn('[Queue] Error rejecting request:', error);
        }
      });
      return;
    }

    if (this.refreshPromise) {
      return;
    }

    const requests = [...this.queuedRequests];
    this.queuedRequests = [];

    requests.forEach(request => {
      if (this.isLoggingOut || this.refreshFailed) {
        try {
          request.reject(new Error('Request cancelled: logout in progress'));
        } catch (error) {
          logger.warn('[Queue] Error rejecting request:', error);
        }
        return;
      }

      const queueRetryConfig = { ...request.config, withCredentials: true };
      queueRetryConfig.baseURL ??= getApiBaseUrl();
      getApiClient()
        .getInstance()
        .request(queueRetryConfig)
        .then(response => {
          request.resolve(response);
        })
        .catch(error => {
          const isTimeout =
            error.code === 'ECONNABORTED' ||
            (typeof error.message === 'string' && error.message.toUpperCase().includes('TIMEOUT'));
          const isNetworkError = !error.response && error.request;

          if (isTimeout || isNetworkError) {
            logger.warn(
              `[Queue] Request ${request.config.url} failed with ${isTimeout ? 'timeout' : 'network error'}, rejecting`
            );
            request.reject(error);
            return;
          }

          if (error?.response?.status === 401 && !this.isLoggingOut && !this.refreshFailed) {
            this.queuedRequests.push(request);
            this.handleUnauthorizedError(error).catch(err => {
              logger.warn('[Queue] handleUnauthorizedError failed:', err);
            });
            return;
          }

          request.reject(error);
        });
    });
  }

  private checkAndLogoutIfNeeded(): boolean {
    if (this.isLoggingOut) {
      return true;
    }

    if (this.getIsLoggedIn && !this.getIsLoggedIn()) {
      return false;
    }

    if (this.refreshPromise) {
      return false;
    }

    const now = Date.now();

    this.recent401Errors = this.recent401Errors.filter(time => now - time < this.MAX_401_WINDOW_MS);
    this.recent401Errors.push(now);

    logger.warn(
      `[401 Counter] Recent 401 errors: ${this.recent401Errors.length}/${this.MAX_401_COUNT} in last ${this.MAX_401_WINDOW_MS}ms`
    );

    if (this.recent401Errors.length >= this.MAX_401_COUNT) {
      logger.warn(
        `[401 Counter] Too many 401 errors (${this.recent401Errors.length}) in short time, logging out`
      );
      this.logoutImmediately();
      return true;
    }

    return false;
  }

  private logoutImmediately(): void {
    if (this.isLoggingOut) {
      return;
    }

    logger.warn('[Logout] Logging out immediately, rejecting all pending requests');
    this.isLoggingOut = true;
    this.refreshPromise = null;
    this.refreshFailed = true;

    const requests = [...this.queuedRequests];
    this.queuedRequests = [];
    requests.forEach(request => {
      try {
        request.reject(new Error('Request cancelled: logout in progress'));
      } catch (error) {
        logger.warn('[Logout] Error rejecting queued request:', error);
      }
    });

    this.recent401Errors = [];

    globalThis.setTimeout(() => this.logoutHandler(), 0);
  }

  private async handleForbiddenError(error: AxiosError): Promise<AxiosError> {
    throw error;
  }
}
